/**
 * Lookbook section.
 * Handles hotspot clicks to show/hide product info cards.
 * Cards are repositioned to stay within viewport bounds.
 */
class LookbookSection extends HTMLElement {
  connectedCallback() {
    this.#bindDesktop();
    this.#bindMobile();
    this.#bindOutsideClick();
  }

  #bindDesktop() {
    this.querySelectorAll('[data-hotspot]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = btn.dataset.hotspot;
        this.#toggle(idx, '[data-card]', '[data-hotspot]');
      });
    });

    this.querySelectorAll('[data-close]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = this.querySelector(`[data-card="${btn.dataset.close}"]`);
        if (card) card.classList.add('hidden');
      });
    });
  }

  #bindMobile() {
    this.querySelectorAll('[data-hotspot-mobile]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = btn.dataset.hotspotMobile;
        this.#toggle(idx, '[data-card-mobile]', '[data-hotspot-mobile]');
      });
    });

    this.querySelectorAll('[data-close-mobile]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = this.querySelector(`[data-card-mobile="${btn.dataset.closeMobile}"]`);
        if (card) card.classList.add('hidden');
      });
    });
  }

  #toggle(idx, cardAttr, hotspotAttr) {
    // Close all other cards in this context
    this.querySelectorAll(cardAttr).forEach((c) => {
      if (c.getAttribute(cardAttr.replace('[', '').replace(']', '')) !== idx) {
        c.classList.add('hidden');
      }
    });

    const attr = cardAttr.replace('[', '').replace(']', '');
    const card = this.querySelector(`${cardAttr.slice(0, -1)}="${idx}"]`);
    if (!card) return;

    const isHidden = card.classList.contains('hidden');
    card.classList.toggle('hidden', !isHidden);

    if (isHidden) {
      // Position card so it stays in bounds
      requestAnimationFrame(() => this.#positionCard(card));
    }
  }

  #positionCard(card) {
    const container = card.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const cardEl = card.querySelector(':scope > div');
    if (!cardEl) return;

    const cardRect = cardEl.getBoundingClientRect();

    // Reset transforms
    card.style.transform = '';

    // Check right overflow
    const rightOverflow = cardRect.right - containerRect.right;
    const bottomOverflow = cardRect.bottom - containerRect.bottom;

    let tx = 10; // offset from dot
    let ty = -cardRect.height / 2;

    if (rightOverflow > 0) {
      tx = -(cardRect.width + 10);
    }

    // Check if card would overflow left
    const leftPos = card.getBoundingClientRect().left + tx;
    if (leftPos < containerRect.left) {
      tx = 10;
    }

    if (bottomOverflow > 0) {
      ty -= bottomOverflow + 10;
    }

    // Check top overflow
    const topPos = card.getBoundingClientRect().top + ty;
    if (topPos < containerRect.top) {
      ty = 10;
    }

    card.style.transform = `translate(${tx}px, ${ty}px)`;
  }

  #bindOutsideClick() {
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.querySelectorAll('.lookbook-card').forEach((c) => c.classList.add('hidden'));
        return;
      }
      // Click on image background (not hotspot/card) closes cards
      if (!e.target.closest('[data-hotspot], [data-hotspot-mobile], .lookbook-card')) {
        this.querySelectorAll('.lookbook-card').forEach((c) => c.classList.add('hidden'));
      }
    });
  }
}

customElements.define('lookbook-section', LookbookSection);

export { LookbookSection };
